<template>
  <v-select
    v-model="selectedOption"
    :items="options"
    :disabled="disabled"
    filled
    background-color="lightGrey"
    max-height="400px"
    no-data-text="Es gibt keine Auswahlmöglichkeiten."
  >
    <template v-slot:label>
      <FieldLabel :mandatory="required" :label="label"></FieldLabel>
    </template>
    <template v-slot:selection="{ attrs, item }">
      <div class="d-flex mt-3 mb-2">
        <v-icon class="mr-2" color="primary">{{ item.icon }}</v-icon>
        <v-list-item-title v-bind="attrs">{{ item.text }}</v-list-item-title>
      </div>
    </template>
    <template v-slot:item="{ item }">
      <v-row class="suus__select-item py-4" no-gutters :style="listStyles">
        <div class="mr-6">
          <v-icon :color="setIconColor(item.value)">{{ item.icon }}</v-icon>
        </div>
        <div>
          <v-list-item-title style="margin-top: 4px" class="font-weight-bold">{{
            item.text
          }}</v-list-item-title>
          <v-list-item-content>
            <div class="contentful__desc" v-html="item.description"></div>
          </v-list-item-content>
        </div>
      </v-row>
    </template>
  </v-select>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from "vue"
import FieldLabel from "components/UI/FieldLabel.vue"
import { mdiCheckCircle } from "@mdi/js"
import { useVuetify } from "features/common/vuetify/useVuetify"

export type ContentfulOption = {
  value: string // Values like Ids, Types, etc (This is emitted as @input)
  text: string // Human Readable
  icon: string // Icon for the item
  description?: string // Description for the item
}

export default defineComponent({
  name: "ContentfulSelect",
  components: { FieldLabel },
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: null,
    },
    options: {
      type: Array as PropType<ContentfulOption[]>,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const selectedOption = ref<string>(props.value)

    watch(
      () => props.value,
      (value) => {
        selectedOption.value = value
      }
    )

    const vuetify = useVuetify()
    const listStyles = {
      borderBottom: `1px solid ${vuetify.theme.themes.light.lightGrey}`,
      flexWrap: "nowrap",
    }

    const setIconColor = (value: string) => {
      return value === selectedOption.value ? "primary" : "grey"
    }

    watch(
      () => selectedOption.value,
      (value) => {
        emit("input", value)
      }
    )

    return {
      selectedOption,
      mdiCheckCircle,
      listStyles,
      setIconColor,
    }
  },
})
</script>

<style lang="sass">

.suus__select-item
  font-family: system-ui

.contentful__desc
  font-size: 14px
  p
    margin-bottom: 8px
</style>
